import React, { useState, useMemo, useCallback } from 'react';
import { X, AlertTriangle, CheckCircle2, FileText, Info, Trash2, Check, Minus } from 'lucide-react';
import { apiService } from '../../services/apiService';

interface AIModel {
  ranked_position?: number;
  id?: string;
  [key: string]: any;
}

interface ParsedModel {
  name: string;
  providerModelId: string;
  customEndpointUrl: string;
  customApiKey: string;
  contextWindow: number;
  maxTokens: number;
  supports_vision: boolean;
  supports_streaming: boolean;
  taskDeterminationModel: boolean;
  isPrivacyCompliant: boolean;
  functionCalling: boolean;
  isUncensored: boolean;
  category: string;
  ranked_position: number | undefined;
  price_per_token: string;
  capabilities: Record<string, number>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (imported: number, errors: { row: number; message: string }[]) => void;
  existingModels: AIModel[];
  isAdmin: boolean;
}

function parseCSVText(text: string): { rows: Record<string, string>[]; error?: string } {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const allLines = text.split(/\r?\n/);
  const cleaned = allLines.filter(line => {
    const t = line.trim();
    if (!t) return false;
    if (t.startsWith('#') || t.startsWith('"#')) return false;
    if (t.toLowerCase().startsWith('sep=')) return false;
    const withoutTrailing = t.replace(/[,;]+$/, '');
    if (withoutTrailing.startsWith('#') || withoutTrailing.startsWith('"#')) return false;
    return true;
  });

  if (cleaned.length < 2) return { rows: [], error: 'CSV has no data rows' };

  const header = cleaned[0];
  const sep = (header.match(/;/g) || []).length > (header.match(/,/g) || []).length ? ';' : ',';
  const headers = header.split(sep).map(h => h.replace(/^\uFEFF/, '').trim().replace(/^"|"$/g, ''));

  if (!headers.some(h => h.toLowerCase() === 'name')) {
    return { rows: [], error: `Could not find "Name" column. Detected columns: ${headers.slice(0, 5).join(', ')}` };
  }

  const rows: Record<string, string>[] = [];
  for (let i = 1; i < cleaned.length; i++) {
    const cells = cleaned[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
    if (cells.every(c => !c)) continue;
    const row: Record<string, string> = {};
    headers.forEach((h, j) => { row[h] = cells[j] ?? ''; });
    rows.push(row);
  }
  return { rows };
}

function getCol(row: Record<string, string>, col: string): string {
  if (row[col] !== undefined) return row[col];
  const lower = col.toLowerCase();
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === lower) return row[k];
  }
  return '';
}

function parseBool(val: string): boolean {
  return ['yes', 'true', '1'].includes(val.trim().toLowerCase());
}

function parseNum(val: string, def: number): number {
  if (!val || val.trim() === '') return def;
  const n = parseInt(val.trim(), 10);
  return isNaN(n) ? def : n;
}

function rowToModel(row: Record<string, string>): ParsedModel {
  const rankStr = getCol(row, 'Ranked Position').trim();
  const streamingVal = getCol(row, 'Supports Streaming');
  return {
    name: getCol(row, 'Name').trim(),
    providerModelId: getCol(row, 'Model ID').trim(),
    customEndpointUrl: getCol(row, 'Endpoint URL').trim(),
    customApiKey: getCol(row, 'API Key').trim(),
    contextWindow: parseNum(getCol(row, 'Context Window'), 32000),
    maxTokens: parseNum(getCol(row, 'Max Tokens'), 4096),
    supports_vision: parseBool(getCol(row, 'Supports Vision')),
    supports_streaming: streamingVal ? parseBool(streamingVal) : true,
    taskDeterminationModel: parseBool(getCol(row, 'Task Determination')),
    isPrivacyCompliant: parseBool(getCol(row, 'Privacy Compliant')),
    functionCalling: parseBool(getCol(row, 'Function Calling')),
    isUncensored: parseBool(getCol(row, 'Uncensored')),
    category: getCol(row, 'Category').trim(),
    ranked_position: rankStr ? parseNum(rankStr, 100) : undefined,
    price_per_token: getCol(row, 'Pricing').trim() || 'Free',
    capabilities: {
      creativity: parseNum(getCol(row, 'Creativity'), 3),
      reasoning: parseNum(getCol(row, 'Reasoning'), 3),
      speed: parseNum(getCol(row, 'Speed'), 3),
      coding: parseNum(getCol(row, 'Coding'), 3),
      webSearch: parseNum(getCol(row, 'WebSearch'), 0),
      enhancedReasoning: parseNum(getCol(row, 'EnhancedReasoning'), 0),
      imageGeneration: parseNum(getCol(row, 'ImageGeneration'), 0),
      conversational: parseNum(getCol(row, 'Conversational'), 3),
    },
  };
}

export default function CSVImportWizard({ isOpen, onClose, onImportComplete, existingModels, isAdmin }: Props) {
  const [parsedModels, setParsedModels] = useState<ParsedModel[]>([]);
  const [parseError, setParseError] = useState('');
  const [hasParsed, setHasParsed] = useState(false);
  const [importing, setImporting] = useState(false);

  const existingRanks = useMemo(
    () => new Set(existingModels.map(m => m.ranked_position).filter((r): r is number => r != null)),
    [existingModels]
  );

  // Map of row index -> conflict type ('existing' = taken by a loaded model, 'duplicate' = appears twice in this CSV)
  const conflicts = useMemo(() => {
    const result = new Map<number, 'existing' | 'duplicate'>();
    const seenInBatch = new Map<number, number>(); // rank -> first row index

    parsedModels.forEach((model, i) => {
      const rank = model.ranked_position;
      if (rank == null) return;
      if (existingRanks.has(rank)) {
        result.set(i, 'existing');
      } else if (seenInBatch.has(rank)) {
        result.set(i, 'duplicate');
        result.set(seenInBatch.get(rank)!, 'duplicate');
      } else {
        seenInBatch.set(rank, i);
      }
    });
    return result;
  }, [parsedModels, existingRanks]);

  const missingFields = useMemo(() => {
    const result = new Set<number>();
    parsedModels.forEach((m, i) => {
      if (!m.name || !m.providerModelId || !m.customEndpointUrl) result.add(i);
    });
    return result;
  }, [parsedModels]);

  const existingModelKeys = useMemo(() => {
    return new Set(
      existingModels
        .filter(m => m.providerModelId && m.customEndpointUrl)
        .map(m => `${m.providerModelId}::${m.customEndpointUrl}`)
    );
  }, [existingModels]);

  const duplicateRows = useMemo(() => {
    const result = new Set<number>();
    parsedModels.forEach((m, i) => {
      if (m.providerModelId && m.customEndpointUrl) {
        if (existingModelKeys.has(`${m.providerModelId}::${m.customEndpointUrl}`)) {
          result.add(i);
        }
      }
    });
    return result;
  }, [parsedModels, existingModelKeys]);

  const hasIssues = conflicts.size > 0 || missingFields.size > 0;

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setParseError('');
    setHasParsed(false);
    setParsedModels([]);

    try {
      const text = await file.text();
      const { rows, error } = parseCSVText(text);
      if (error) { setParseError(error); return; }
      if (rows.length === 0) { setParseError('No data rows found in CSV'); return; }
      setParsedModels(rows.map(rowToModel));
      setHasParsed(true);
    } catch (err: any) {
      setParseError(err.message || 'Failed to read file');
    }
  }, []);

  const handleRankChange = useCallback((index: number, value: string) => {
    const trimmed = value.trim();
    const rank = trimmed === '' ? undefined : parseInt(trimmed, 10);
    setParsedModels(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        ranked_position: rank !== undefined && isNaN(rank) ? undefined : rank,
      };
      return updated;
    });
  }, []);

  const handleDeleteRow = useCallback((index: number) => {
    setParsedModels(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAutoAssign = useCallback(() => {
    setParsedModels(prev => {
      // Recompute conflicts from the current state (not the stale memo)
      const conflictingRows = new Set<number>();
      const seenInBatch = new Map<number, number>();

      prev.forEach((model, i) => {
        const rank = model.ranked_position;
        if (rank == null) return;
        if (existingRanks.has(rank)) {
          conflictingRows.add(i);
        } else if (seenInBatch.has(rank)) {
          conflictingRows.add(i);
          conflictingRows.add(seenInBatch.get(rank)!);
        } else {
          seenInBatch.set(rank, i);
        }
      });

      // Seed used ranks with existing models + all non-conflicting CSV rows
      const usedRanks = new Set(existingRanks);
      prev.forEach((m, i) => {
        if (!conflictingRows.has(i) && m.ranked_position != null) {
          usedRanks.add(m.ranked_position);
        }
      });

      const nextFree = () => {
        let rank = 1;
        while (usedRanks.has(rank)) rank++;
        usedRanks.add(rank);
        return rank;
      };

      const updated = [...prev];
      prev.forEach((_, i) => {
        if (conflictingRows.has(i)) {
          updated[i] = { ...updated[i], ranked_position: nextFree() };
        }
      });
      return updated;
    });
  }, [existingRanks]);

  const handleImport = async () => {
    setImporting(true);
    const errors: { row: number; message: string }[] = [];
    let imported = 0;

    for (let i = 0; i < parsedModels.length; i++) {
      const m = parsedModels[i];
      try {
        await apiService.post('/portal/models', {
          id: `${m.providerModelId}-${Date.now()}-${i}`,
          name: m.name,
          providerModelId: m.providerModelId,
          provider: 'custom',
          isCustomEndpoint: true,
          customEndpointUrl: m.customEndpointUrl,
          customApiKey: m.customApiKey,
          contextWindow: m.contextWindow,
          maxTokens: m.maxTokens,
          supports_vision: m.supports_vision,
          supports_streaming: m.supports_streaming,
          taskDeterminationModel: m.taskDeterminationModel,
          isPrivacyCompliant: m.isPrivacyCompliant,
          functionCalling: m.functionCalling,
          isUncensored: m.isUncensored,
          category: m.category || undefined,
          ranked_position: m.ranked_position,
          price_per_token: isAdmin ? m.price_per_token : 'Free',
          capabilities: m.capabilities,
        });
        imported++;
      } catch (err: any) {
        const msg = err.response?.data?.error?.message || err.message || 'Unknown error';
        errors.push({ row: i + 1, message: `${m.name || `Row ${i + 1}`}: ${msg}` });
      }
    }

    setImporting(false);
    onImportComplete(imported, errors);
  };

  const handleClose = () => {
    if (importing) return;
    setParsedModels([]);
    setParseError('');
    setHasParsed(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999, padding: 16 }} onClick={onClose}>
      <div className="pk-modal" style={{ maxWidth: 1120, maxHeight: '95vh' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="pk-modal-head" style={{ justifyContent: 'space-between' }}>
          <div className="col">
            <h2 className="pk-h3">Import Models from CSV</h2>
            {hasParsed && (
              <p className="pk-sub">
                {parsedModels.length} model{parsedModels.length !== 1 ? 's' : ''} parsed
                {conflicts.size > 0 && (
                  <span style={{ marginLeft: 8, color: 'var(--arc-danger)', fontWeight: 700 }}>
                    · {conflicts.size} rank conflict{conflicts.size !== 1 ? 's' : ''}
                  </span>
                )}
                {missingFields.size > 0 && (
                  <span style={{ marginLeft: 8, color: 'var(--arc-warning)', fontWeight: 700 }}>
                    · {missingFields.size} row{missingFields.size !== 1 ? 's' : ''} missing required fields
                  </span>
                )}
                {duplicateRows.size > 0 && (
                  <span style={{ marginLeft: 8, color: 'var(--arc-warning)', fontWeight: 700 }}>
                    · {duplicateRows.size} already exist
                  </span>
                )}
              </p>
            )}
          </div>
          <button type="button" onClick={handleClose} disabled={importing} className="pk-btn pk-btn-quiet pk-btn-icon"><X size={20} /></button>
        </div>

        <div className="pk-modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

          {/* File picker — shown before a file is parsed */}
          {!hasParsed && (
            <div className="pk-empty">
              <div className="pk-i-plate"><FileText size={18} /></div>
              <p className="pk-sub">Select a CSV file to preview all models before importing.</p>
              <p className="pk-small">Rank conflicts are checked locally — nothing is sent until you confirm.</p>
              <label className="pk-btn pk-btn-ink" style={{ cursor: 'pointer' }}>
                Choose CSV File
                <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
              </label>
              {parseError && (
                <div className="pk-note pk-note-danger" style={{ marginTop: 8 }}>{parseError}</div>
              )}
            </div>
          )}

          {/* Replace-file link + auto-assign + conflict banner — shown after parsing */}
          {hasParsed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <label className="pk-btn-text" style={{ cursor: 'pointer' }}>
                Choose a different file
                <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
              </label>
              {hasIssues && (
                <div className="pk-note pk-note-warning" style={{ padding: '8px 12px' }}>
                  <AlertTriangle size={14} />
                  <span className="pk-small">Edit the <strong>Rank</strong> column below to resolve conflicts, then import</span>
                </div>
              )}
            </div>
          )}

          {/* Preview table */}
          {hasParsed && parsedModels.length > 0 && (
            <div className="pk-table-wrap">
              <div className="pk-table-scroll" style={{ maxHeight: '55vh' }}>
                <table className="pk-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Model ID</th>
                      <th>Endpoint URL</th>
                      <th>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ textTransform: 'none' }}>
                            Rank
                            <span style={{ marginLeft: 4, fontWeight: 400, color: 'var(--arc-accent)' }}>(editable)</span>
                          </span>
                          {conflicts.size > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <button
                                type="button"
                                onClick={handleAutoAssign}
                                className="pk-badge pk-badge-accent"
                                style={{ textTransform: 'none', cursor: 'pointer', border: 0 }}
                              >
                                Auto Assign
                              </button>
                              <span title="Fills all conflicting ranks with the next available numbers, starting from 1."><Info size={12} className="pk-i" /></span>
                            </div>
                          )}
                        </div>
                      </th>
                      <th>Category</th>
                      <th>Vision</th>
                      <th>Task Det.</th>
                      <th>Privacy</th>
                      <th>Fn Calling</th>
                      <th style={{ position: 'sticky', right: 0, background: 'var(--arc-bg-2)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedModels.map((model, i) => {
                      const conflict = conflicts.get(i);
                      const isDuplicate = duplicateRows.has(i);
                      const missing = missingFields.has(i);
                      const rowBg = conflict ? 'var(--arc-danger-soft)' : missing ? 'var(--arc-warning-soft)' : isDuplicate ? 'var(--arc-warning-soft)' : undefined;
                      return (
                        <tr key={i} style={{ background: rowBg }}>
                          <td className="id">{i + 1}</td>
                          <td className="strong">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {model.name || <span style={{ color: 'var(--arc-danger)', fontStyle: 'italic', fontWeight: 400 }} className="pk-small">missing</span>}
                              {isDuplicate && (
                                <span className="pk-badge pk-badge-warning" style={{ whiteSpace: 'nowrap' }}>already exists</span>
                              )}
                            </div>
                          </td>
                          <td className="id" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={model.providerModelId}>
                            {model.providerModelId || <span style={{ color: 'var(--arc-danger)', fontStyle: 'italic' }}>missing</span>}
                          </td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={model.customEndpointUrl}>
                            {model.customEndpointUrl || <span style={{ color: 'var(--arc-danger)', fontStyle: 'italic' }}>missing</span>}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="number"
                                min={1}
                                value={model.ranked_position ?? ''}
                                onChange={e => handleRankChange(i, e.target.value)}
                                className="pk-input pk-small"
                                style={{ width: 64, minHeight: 30 }}
                                aria-invalid={!!conflict}
                                placeholder="—"
                              />
                              {conflict && (
                                <span className="pk-small" style={{ color: 'var(--arc-danger)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  {conflict === 'existing' ? '← taken' : '← duplicate'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{model.category || '—'}</td>
                          <td style={{ textAlign: 'center' }}>{model.supports_vision ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                          <td style={{ textAlign: 'center' }}>{model.taskDeterminationModel ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                          <td style={{ textAlign: 'center' }}>{model.isPrivacyCompliant ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                          <td style={{ textAlign: 'center' }}>{model.functionCalling ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                          <td className="pk-td-sticky" style={{ right: 0, textAlign: 'center', background: rowBg || 'var(--arc-paper)' }}>
                            <button type="button" onClick={() => handleDeleteRow(i)} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm" style={{ color: 'var(--arc-danger)' }} title="Remove this row">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {hasParsed && (
          <div className="pk-modal-foot" style={{ alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {hasIssues ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--arc-danger)' }} className="pk-small">
                  <AlertTriangle size={14} />
                  {conflicts.size > 0 && `${conflicts.size} rank conflict${conflicts.size !== 1 ? 's' : ''}`}
                  {conflicts.size > 0 && missingFields.size > 0 && ' · '}
                  {missingFields.size > 0 && `${missingFields.size} row${missingFields.size !== 1 ? 's' : ''} missing required fields`}
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--arc-success)' }} className="pk-small">
                  <CheckCircle2 size={14} />
                  Ready to import {parsedModels.length} model{parsedModels.length !== 1 ? 's' : ''}
                </span>
              )}
              {duplicateRows.size > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--arc-warning)' }} className="pk-small">
                  <Info size={12} />
                  {duplicateRows.size} row{duplicateRows.size !== 1 ? 's' : ''} already exist and will be added as duplicates — delete them above to skip
                </span>
              )}
            </div>
            <div className="spacer" />
            <button type="button" onClick={handleClose} disabled={importing} className="pk-btn pk-btn-outline">
              Cancel
            </button>
            <button type="button" onClick={handleImport} disabled={hasIssues || importing} className="pk-btn pk-btn-ink">
              {importing
                ? 'Importing...'
                : `Import ${parsedModels.length} Model${parsedModels.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
